import { useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

import { AlertType } from '@app/components/Alert';
import Alerts from '@app/components/Alerts';
import { ParsedContentFormat } from '@app/components/ParsedContent';
import { isNotNullish } from '@app/helpers/predicate';
import { cmsBlockToAlertItem, getEnabledNotices } from '@app/helpers/shop';
import useAlerts from '@app/hooks/useAlerts';
import type {
  CartQuery_magento2_cart_items as CartItem,
  CartQuery_magento2_cart,
} from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';

import type {
  ShopStoreNoticesQuery_magento2_cmsBlocks_items as Notice,
  ShopStoreNoticesQuery,
} from './__generated__/ShopStoreNoticesQuery';

const ShopStoreAlerts = ({
  items,
}: {
  items?: CartQuery_magento2_cart['items'];
}) => {
  const { alerts, addAlerts } = useAlerts([]);

  const { data } = useQuery<ShopStoreNoticesQuery>(shopStoreNoticesQuery, {
    fetchPolicy: 'cache-first',
  });

  const notices = useMemo(() => {
    const notices = getEnabledNotices(data);
    return notices && getCartNotices(items, notices);
  }, [items, data]);

  useEffect(() => {
    if (notices?.length) {
      addAlerts(
        notices.map((cmsBlock) =>
          cmsBlockToAlertItem({
            type: AlertType.Warning,
            cmsBlock,
            format:
              cmsBlock?.identifier === 'cart-hardware-delivery'
                ? ParsedContentFormat.ParseReact
                : ParsedContentFormat.PlainText,
          }),
        ),
      );
    }
  }, [notices, addAlerts]);

  return <Alerts alerts={alerts} />;
};

export default ShopStoreAlerts;

const getCartNotices = (
  items: (CartItem | null)[] | null | undefined,
  notices: Notice[],
): Notice[] => {
  const getNotice = (id: string) =>
    notices.find((notice) => notice.identifier === id) || null;

  const hardwareNotices = [
    getNotice('cart-hardware-notice'),
    getNotice('cart-hardware-notice2'),
    getNotice('cart-hardware-delivery'),
  ];

  const hardwareItems = items
    ?.filter((item) => item?.__typename === 'Magento2_SimpleCartItem')
    .filter(isNotNullish);

  const virtualItems = items
    ?.filter(
      (item) =>
        item?.__typename === 'Magento2_BundleCartItem' ||
        item?.__typename === 'Magento2_VirtualCartItem',
    )
    .filter(isNotNullish);

  return [
    getNotice('cart-general-notice'),
    virtualItems?.length ? getNotice('cart-software-notice') : null,
    ...(hardwareItems?.length ? hardwareNotices : []),
  ].filter(isNotNullish);
};

export const shopStoreNoticesQuery = gql`
  query ShopStoreNoticesQuery {
    magento2 {
      cmsBlocks(
        identifiers: [
          "cart-general-notice"
          "cart-hardware-notice"
          "cart-hardware-notice2"
          "cart-software-notice"
          "cart-hardware-delivery"
        ]
      ) {
        items {
          title
          identifier
          content
        }
      }
    }
  }
`;
